import { Alert, Col, CollapseProps, Flex, Spin, Typography } from "antd";

import { CustomButton } from "ui";
import { ReactComponent as CheckIcon } from "images/check.svg";
import { ItemInfoCopy } from "components/ItemInfo";
import { getColumns } from "./getColumns";
import { DeleteBankDetails } from "./DeleteBankDetails";
import { getStatusMessage } from "services";
import { parseErrorMessage } from "utils";
import { BankAccountGrid } from "interfaces";
import { BankAccountsDetailStore } from "stores/bankAccountsDetailStore";

import styles from "./bank.module.css";

interface BankAccountCollapseItemProps {
  item: BankAccountGrid;
  showDefaultAccChangeButton: boolean;
  loading: boolean;
  canArchiveBankAccount: boolean;
  bankAccountsLength: number;
  onSetDefault: (id: string) => void;
  bankAccountsDetailStore: BankAccountsDetailStore;
  isSafeDealMode?: boolean;
}

export const getBankAccountCollapseItem = ({
  item,
  showDefaultAccChangeButton,
  loading,
  canArchiveBankAccount,
  bankAccountsLength,
  onSetDefault,
  bankAccountsDetailStore,
  isSafeDealMode,
}: BankAccountCollapseItemProps): CollapseProps["items"] => {
  return [
    {
      key: item.id,
      label: (
        <Flex align="start" gap={"middle"}>
          <Flex align="center" justify="space-between" className={`flex-grow ${styles.label}`}>
            <Col flex="1" className="full-width">
              <Flex gap="small" align="start" justify="space-between" className="full-width">
                <Flex vertical gap="3px">
                  <Typography.Title level={5} className={`margin-none`}>
                    {item.accountName}
                  </Typography.Title>
                  <Typography.Text className={`text-description ${styles.labelDescription}`}>
                    <Flex gap="small">
                      <span>{item.bankName}</span>
                      <span>•</span>
                      <span>{item.accountNumber}</span>
                    </Flex>
                  </Typography.Text>
                </Flex>

                <Flex gap="small" align="center" className="padding-horizontal-md">
                  {item.isDefault ? (
                    <CustomButton
                      type="default"
                      size="small"
                      disabled
                      className={styles.defaultButton}
                      icon={<CheckIcon className={styles.checkIcon} />}
                    >
                      Счёт по умолчанию
                    </CustomButton>
                  ) : (
                    showDefaultAccChangeButton && (
                      <CustomButton
                        type="default"
                        size="small"
                        disabled={loading}
                        onClick={() => onSetDefault(item.id)}
                      >
                        Сделать счётом по умолчанию
                      </CustomButton>
                    )
                  )}

                  {bankAccountsLength > 1 && canArchiveBankAccount && !isSafeDealMode && (
                    <DeleteBankDetails id={item.id} />
                  )}
                </Flex>
              </Flex>
            </Col>
          </Flex>
        </Flex>
      ),
      children: (
        <Spin spinning={bankAccountsDetailStore.isPending && !bankAccountsDetailStore.getBankAccountDetails(item.id)}>
          {((bankAccountsDetailStore.fetchStatus === "success" &&
            bankAccountsDetailStore.getBankAccountDetails(item.id)) ||
            bankAccountsDetailStore.getBankAccountDetails(item.id)) && (
            <Flex vertical>
              {getColumns(bankAccountsDetailStore.getBankAccountDetails(item.id)!).map((column, index) => (
                <ItemInfoCopy
                  key={column?.id || `column-${index}`}
                  title={column?.label}
                  text={String(column?.text || "")}
                  id={column?.id}
                  horizontal={true}
                />
              ))}
            </Flex>
          )}
          {bankAccountsDetailStore.fetchStatus === "error" && bankAccountsDetailStore.fetchError && (
            <Alert
              message={getStatusMessage(bankAccountsDetailStore.fetchError.status)}
              description={parseErrorMessage(bankAccountsDetailStore.fetchError)}
              type="error"
              showIcon
              className="margin-top-lg"
            />
          )}
        </Spin>
      ),
    },
  ];
};
